import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSessionId } from '../utils/session';

export default function CartCreditBanner() {
  const { user } = useAuth();
  const [pendingCredits, setPendingCredits] = useState([]);
  const [totalCredits, setTotalCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);

  // Define fetchPendingCredits BEFORE using it in useEffect
  const fetchPendingCredits = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/session/pending-credits', {
        headers: {
          'X-Session-Id': getSessionId()
        }
      });
      if (response.ok) {
        const data = await response.json();
        setPendingCredits(data.pendingCredits || []);
        setTotalCredits(data.total || 0);
      }
    } catch (error) {
      console.error('Error fetching pending credits:', error);
    } finally {
      setLoading(false);
    }
  };

  // Now useEffect can safely call fetchPendingCredits
  useEffect(() => {
    fetchPendingCredits();
  }, []);

  // If user is logged in, don't show
  if (user) return null;

  const handleSendEmail = async () => {
    if (!email) return;
    try {
      const response = await fetch('/api/session/send-reminder', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Session-Id': getSessionId()
        },
        body: JSON.stringify({ email })
      });
      if (response.ok) {
        setEmailSent(true);
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  if (loading) return null;
  if (totalCredits === 0) return null;

  return (
    <div className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 rounded-lg p-6 mb-6">
      <div className="flex items-start gap-4">
        <div className="text-4xl">🎉</div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-green-700">
            You have {totalCredits} credit{totalCredits > 1 ? 's' : ''} waiting!
          </h3>
          <p className="text-gray-600 mt-1">
            {pendingCredits.map((p, i) => (
              <span key={i}>
                {i > 0 && ', '}
                {p.amount} from {p.vendorId?.name || 'a vendor'}
              </span>
            ))}
          </p>
          <p className="text-sm text-gray-500 mt-2">
            💡 Register to claim your credits and start shopping!
          </p>

          <div className="flex flex-wrap gap-3 mt-4">
            <Link
              to="/register"
              className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition"
            >
              Register & Claim →
            </Link>
            <button
              onClick={() => setShowEmailForm(!showEmailForm)}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              or enter email to save your credits
            </button>
          </div>

          {showEmailForm && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
              {emailSent ? (
                <p className="text-green-600 text-sm">
                  ✅ Check your email for redemption details!
                </p>
              ) : (
                <>
                  <p className="text-sm text-gray-600 mb-2">
                    Enter your email and we'll send you a link to claim your credits:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="flex-1 border rounded-lg px-3 py-2 text-sm"
                    />
                    <button
                      onClick={handleSendEmail}
                      disabled={!email}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3">
            ⏰ Credits expire 30 days after earning.
          </p>
        </div>
      </div>
    </div>
  );
}
